import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { unwrapResult } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { SelectChangeEvent } from '@mui/material';

import {
	CustomButton,
	CustomCheckBox,
	CustomRadio,
	CustomSelect,
	CustomSwitch,
	CustomTable,
	Text,
} from '../../../../../shared/uiComponents';
import { SelectDataProperties } from '../../../../../shared/uiComponents/Dropdown';
import { errorNormalizer } from '../../../../../shared/Helpers/functions';
import { AdminTypes } from '../../../../../components/Menu';
import { TableBody, headers } from './tableInfo';
import './styles.scss';
import Allocate from './allocate';
import { AllocationContext } from './AllocationContext';

import { DispatchProperties, useSelector } from '../../../../../redux/store';
import { getBillingStatus, getTherapists, updateBilling } from '../../../../../redux/State/clientSlice/userClientSlice';
import {
	getAllocationPageDataBcba,
	getAllocationPageDataRbt,
} from '../../../../../redux/State/clientSlice/insuranceAuthorizationSlice';
import { AllocationPageAuthorizationProperties } from '../../../../../redux/API/ClientAPIHelpers/insuranceAuthorizationProperties';
import DefaultBCBA from '../../../AssignTherapists/defaultBCBA';

const OPTIONS = [
	{ id: '0', title: 'For BCBA' },
	{ id: '1', title: 'For BT' },
];

const getSessionTypeSortOrder = (name: string) => {
	const normalized = name.toLowerCase();
	if (normalized.includes('treatment planning') || normalized.includes('h0032')) return 2;
	if (normalized.includes('assessment') || normalized.includes('97151')) return 1;
	if (normalized.includes('97153') || normalized.includes('behavior treatment')) return 3;

	if (
		normalized.includes('97155') ||
		normalized.includes('direct supervision') ||
		normalized.includes('behavior treatment modification') ||
		normalized.includes('dsu') ||
		normalized.includes('btm')
	) {
		return 4;
	}
	if (normalized.includes('97156') || normalized.includes('parent training')) return 5;

	return 99;
};

const AllocateInsuranceClinicalSupervisor = () => {
	const dispatch = useDispatch<DispatchProperties>();
	const { clientId } = useParams();

	const clinicallySupervisedById = useSelector((state) => state.account.user.id);
	const therapists = useSelector((state) => state.userClient.therapists);

	const allocationPageData = useSelector((state) => state.authorization.allocationPageData);
	const allocationPageLoading = useSelector((state) => state.authorization.allocationPageLoading);

	const [userId, setUserId] = useState<string>('');
	const [authorizations, setAuthorizations] = useState<AllocationPageAuthorizationProperties[]>([]);
	const [billingStatus, setBillingStatus] = useState<boolean>(false);
	const [billingLoading, setBillingLoading] = useState<boolean>(false);

	const fetchAllocationData = useCallback(() => {
		if (!clientId) return;
		dispatch(getAllocationPageDataBcba({ clientId, includeExpired: null }));
	}, [clientId, dispatch]);

	useEffect(() => {
		fetchAllocationData();
	}, [fetchAllocationData]);

	useEffect(() => {
		if (!allocationPageData) {
			setAuthorizations([]);

			return;
		}
		const filteredAuthorizations: AllocationPageAuthorizationProperties[] = [];

		for (const auth of allocationPageData.authorizations) {
			const filteredDetails = auth.details.filter((detail) =>
				detail.sessionType.name.toLowerCase().includes('assessment'),
			);

			if (filteredDetails.length) {
				const sortedDetails = [...filteredDetails].sort((a, b) => {
					const aOrder = getSessionTypeSortOrder(a.sessionType.name);
					const bOrder = getSessionTypeSortOrder(b.sessionType.name);
					if (aOrder !== bOrder) return aOrder - bOrder;

					return a.sessionType.name.localeCompare(b.sessionType.name);
				});
				filteredAuthorizations.push({
					...auth,
					details: sortedDetails,
				});
			}
		}
		setAuthorizations(filteredAuthorizations);
	}, [allocationPageData]);

	useEffect(() => {
		if (!userId || !clientId) return;
		setBillingLoading(true);
		dispatch(getBillingStatus({ clientId, userId }))
			.then(unwrapResult)
			.then((response) => setBillingStatus(response))
			.catch(errorNormalizer)
			.finally(() => setBillingLoading(false));
	}, [userId, clientId, dispatch]);

	useEffect(() => {
		if (!clientId) return;
		dispatch(getTherapists({ clientId, clinicallySupervisedById }));
	}, [clientId, clinicallySupervisedById, dispatch]);

	const onChangeTherapist = (event: SelectChangeEvent<string>) => {
		const { value } = event.target;
		setUserId(value);
	};

	const updateBillingStatus = (billingStopped: boolean) => {
		if (!userId || !clientId) return;
		setBillingStatus(billingStopped);
		dispatch(updateBilling({ clientId, userId, billingStopped }))
			.then(unwrapResult)
			.then(({ isSuccessful }) => {
				toast(isSuccessful ? 'Updated' : 'Failed');
			})
			.catch(errorNormalizer);
	};

	const therapistList = therapists
		? therapists.map((x) => ({
				id: x.id,
				name: `${x.fullName} (${x.role.name})`,
			}))
		: [];

	const contextValue = useMemo(() => ({ refetchAllocationData: fetchAllocationData }), [fetchAllocationData]);

	return (
		<AllocationContext.Provider value={contextValue}>
			<div className="allocationWrapper marginBottom16 marginTop16">
				<CustomSelect
					label="Select Provider"
					data={therapistList}
					value={userId}
					setValue={onChangeTherapist}
					className="marginBottom16 marginTop16"
				/>
				{Boolean(userId) && (
					<CustomCheckBox
						item={{
							id: '0',
							checked: billingStatus,
							label: 'Lock BCBA Billing',
							disabled: billingLoading,
						}}
						onChange={(event) => updateBillingStatus(event.target.checked)}
						style={{ marginBottom: '16px' }}
					/>
				)}
				<Allocate therapistId={userId} />
			</div>
			{!allocationPageLoading && !authorizations.length ? (
				<div className="allocationWrapper">
					<Text title="Client does not have active insurances" className="marginBottom16" />
				</div>
			) : (
				<CustomTable
					TableBody={TableBody}
					headers={headers}
					data={{ query: authorizations }}
					loading={allocationPageLoading}
					hasPagination={false}
				/>
			)}
		</AllocationContext.Provider>
	);
};

const AllocateInsurance = () => {
	const dispatch = useDispatch<DispatchProperties>();
	const navigate = useNavigate();
	const { clientId } = useParams();
	const [params, setParams] = useSearchParams();

	const therapists = useSelector((state) => state.userClient.therapists);
	const role = useSelector((state) => state.account.role);
	const isSuperAdmin = role?.name === 'super-admin';

	const allocationPageData = useSelector((state) => state.authorization.allocationPageData);
	const allocationPageLoading = useSelector((state) => state.authorization.allocationPageLoading);

	const [userId, setUserId] = useState<string>('');
	const [option, setOption] = useState<string>('1');
	const [includeExpired, setIncludeExpired] = useState<boolean | null>(null);
	const [therapistsDataBCBA, setTherapistsDataBCBA] = useState<SelectDataProperties[]>([]);
	const [therapistsDataBT, setTherapistsDataBT] = useState<SelectDataProperties[]>([]);
	const [authorizations, setAuthorizations] = useState<AllocationPageAuthorizationProperties[]>([]);
	const [billingStatus, setBillingStatus] = useState<boolean>(false);
	const [billingLoading, setBillingLoading] = useState<boolean>(false);

	useEffect(() => {
		const canViewExpired = role?.name === 'super-admin';
		if (!canViewExpired) return;
		setIncludeExpired(canViewExpired);
	}, [role]);

	const fetchAllocationData = useCallback(() => {
		if (!clientId) return;
		const isRbt = option === '1';
		const fetchAction = isRbt ? getAllocationPageDataRbt : getAllocationPageDataBcba;
		dispatch(fetchAction({ clientId, includeExpired }));
	}, [clientId, includeExpired, dispatch, option]);

	useEffect(() => {
		fetchAllocationData();
	}, [fetchAllocationData]);

	useEffect(() => {
		if (!allocationPageData) {
			setAuthorizations([]);

			return;
		}
		const sortedAuthorizations = allocationPageData.authorizations.map((auth) => ({
			...auth,
			details: [...auth.details].sort((a, b) => {
				const aOrder = getSessionTypeSortOrder(a.sessionType.name);
				const bOrder = getSessionTypeSortOrder(b.sessionType.name);
				if (aOrder !== bOrder) return aOrder - bOrder;

				return a.sessionType.name.localeCompare(b.sessionType.name);
			}),
		}));
		setAuthorizations(sortedAuthorizations);
	}, [allocationPageData]);

	useEffect(() => {
		if (!userId || !clientId) return;
		setBillingLoading(true);
		dispatch(getBillingStatus({ clientId, userId }))
			.then(unwrapResult)
			.then((response) => setBillingStatus(response))
			.catch(errorNormalizer)
			.finally(() => setBillingLoading(false));
	}, [userId, clientId, dispatch]);

	useEffect(() => {
		if (!therapists) return;
		const dataBCBA: SelectDataProperties[] = therapists
			.filter((x) => x.role.section.id !== AdminTypes.bt)
			.map((x) => ({
				id: x.id,
				name: `${x.fullName} (${x.role.name})`,
			}));
		const dataBT: SelectDataProperties[] = therapists
			.filter((x) => x.role.section.id === AdminTypes.bt)
			.map((x) => ({
				id: x.id,
				name: `${x.fullName}`,
			}));
		setTherapistsDataBCBA(dataBCBA);
		setTherapistsDataBT(dataBT);
	}, [therapists]);

	useEffect(() => {
		if (!clientId) return;
		dispatch(getTherapists({ clientId }));
	}, [clientId, dispatch]);

	const onChangeTherapist = (event: SelectChangeEvent<string>) => {
		const { value } = event.target;
		setUserId(value);
	};

	const navigateAddAuthorizationHandler = () => {
		window.localStorage.setItem('tab', '2');
		navigate(`/clients/client/${clientId}`);
	};

	const therapistRadioHandler = (_: ChangeEvent<HTMLInputElement>, value: string) => {
		setOption(value);
		params.delete('authId');
		params.delete('detailsId');
		setParams(params);
		setUserId('');
	};

	const includeExpiredHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { checked } = event.target;
		setIncludeExpired(checked);
	};

	const updateBillingStatus = (billingStopped: boolean) => {
		if (!userId || !clientId) return;
		setBillingStatus(billingStopped);
		dispatch(updateBilling({ clientId, userId, billingStopped }))
			.then(unwrapResult)
			.then(({ isSuccessful }) => {
				toast(isSuccessful ? 'Updated' : 'Failed');
			})
			.catch(errorNormalizer);
	};

	const showBillingCheckbox = therapists?.find((x) => x.id === userId)?.role.section.id === AdminTypes.bcba;

	const contextValue = useMemo(() => ({ refetchAllocationData: fetchAllocationData }), [fetchAllocationData]);

	return (
		<AllocationContext.Provider value={contextValue}>
			<div className="allocationPageHeader">
				<div className="allocationControlsSection">
					<div className="allocationRadioGroup">
						{OPTIONS.map((x) => (
							<CustomRadio key={x.id} id={x.id} label={x.title} value={option} onChange={therapistRadioHandler} />
						))}
					</div>
					<CustomSelect
						data={option === '0' ? therapistsDataBCBA : therapistsDataBT}
						value={userId}
						setValue={onChangeTherapist}
					/>
					{showBillingCheckbox && (
						<CustomCheckBox
							item={{
								id: '0',
								checked: billingStatus,
								label: 'Lock BCBA Billing',
								disabled: billingLoading,
							}}
							onChange={(event) => updateBillingStatus(event.target.checked)}
						/>
					)}
					<Allocate therapistId={userId} />
				</div>
				<div className="allocationActionsSection">
					<DefaultBCBA />
					{isSuperAdmin && typeof includeExpired === 'boolean' && (
						<CustomSwitch label="Show Expired" checked={includeExpired} onChange={includeExpiredHandler} />
					)}
				</div>
			</div>
			{!allocationPageLoading && !authorizations.length ? (
				<div className="allocationWrapper marginTop16">
					<Text
						title={includeExpired ? 'No authorizations found' : 'Client does not have active authorizations'}
						type="plg"
						fontWeight="semiBold"
						className="marginBottom16"
					/>
					<CustomButton title="Add Authorization" onClick={navigateAddAuthorizationHandler} />
				</div>
			) : (
				<CustomTable
					TableBody={TableBody}
					headers={headers}
					data={{ query: authorizations }}
					loading={allocationPageLoading}
					hasPagination={false}
					hasMobile={false}
				/>
			)}
		</AllocationContext.Provider>
	);
};

const AllocateHoursWrapper = () => {
	const role = useSelector((state) => state.account.role);

	if (role?.section.id !== AdminTypes.cs) return <AllocateInsurance />;

	return <AllocateInsuranceClinicalSupervisor />;
};

export default AllocateHoursWrapper;
